import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AnswerRequest, Form } from "../dynamicForm";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import type { RiskById, RisksGridView } from "./risks.types";
import type { DpiaRiskAssessmentPayload } from "./dpia.types";
import { prefetch } from "./prefetch";

const BASE_ENDPOINT_RISK = `${FORMS_API_BASE_ENDPOINT}/dpia/riskassessment`;

const risksKeys = {
  all: () => ["risks"] as const,
  byId: (id: string) => [...risksKeys.all(), id] as const,
  gridView: () => [...risksKeys.all(), "gridview"] as const,
  form: () => [...risksKeys.all(), "form"] as const,
};

/** Get risks by id */
const riskByIdQueryOptions = (id: string) =>
  queryOptions({
    queryKey: risksKeys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT_RISK}/${id}`) as Promise<RiskById>,
    throwOnError: true,
  });

export const useGetRiskById = (id: string) => useQuery(riskByIdQueryOptions(id));

/** Get risks form */
const riskFormQueryOptions = () =>
  queryOptions({
    queryKey: risksKeys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT_RISK}/form`) as Promise<Form>,
    throwOnError: true,
  });

export const useGetRiskForm = () => useQuery(riskFormQueryOptions());
export const prefetchRiskForm = () => prefetch(riskFormQueryOptions());

/** Update risk answers */
export const useUpdateRiskAnswers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ riskId, answers }: { riskId: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT_RISK}/${riskId}/answers`, answers) as Promise<RiskById>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

/** Get risk grid view */
const risksGridViewQueryOptions = () =>
  queryOptions({
    queryKey: risksKeys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT_RISK}/gridview`) as Promise<RisksGridView>,
    throwOnError: true,
  });
export const useGetRisksGridView = () => useQuery(risksGridViewQueryOptions());
export const prefetchRisksGridView = () => prefetch(risksGridViewQueryOptions());

/** update risk description */
export const useUpdateRiskDescription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DpiaRiskAssessmentPayload) => api.post(`${BASE_ENDPOINT_RISK}`, payload) as Promise<RiskById>,
    onSuccess: () => queryClient.invalidateQueries(),
  });
};
